import { QueryClient, useQuery, useInfiniteQuery } from 'react-query';
import { stroot } from '@birdi/js-sdk';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import type {
  MapSiteListItem,
  MapSite,
  MapSiteMapRow,
  MapSiteReferenceSet,
  SiteDemLayer,
  ToleranceResponse,
  ToleranceVersion,
} from '@birdi/js-sdk/src/public/map-sites';

/**
 * One QueryClient shared by every Map Sites surface (sidebar section, All
 * Sites page, Site page) so the sites list is fetched once and cache
 * invalidations propagate everywhere.
 */
export const mapSitesQueryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

export const SITES_LIST_QUERY_KEY = ['mapSites', 'list'] as const;
export const siteQueryKey = (id: string) => ['mapSites', 'site', id] as const;
export const siteMapsQueryKey = (id: string) =>
  ['mapSites', 'siteMaps', id] as const;
export const referenceSetsQueryKey = (id: string) =>
  ['mapSites', 'referenceSets', id] as const;

export const useSitesList = () =>
  useQuery<MapSiteListItem[]>(SITES_LIST_QUERY_KEY, () =>
    mapSitesAPI.listSites(stroot('phie2m')).then((res) => res.body ?? []),
  );

export const useSite = (id: string) =>
  useQuery<MapSite | undefined>(siteQueryKey(id), () =>
    mapSitesAPI.getSite(stroot('aico2b'), id).then((res) => res.body),
  );

export const SITE_MAPS_PAGE_SIZE = 10;

// Paginated ("load more") so a site with many maps doesn't fetch/enrich them
// all at once. Each page returns up to SITE_MAPS_PAGE_SIZE rows.
export const useSiteMaps = (id: string) =>
  useInfiniteQuery<MapSiteMapRow[]>(
    siteMapsQueryKey(id),
    ({ pageParam = 1 }) =>
      mapSitesAPI
        .listSiteMaps(stroot('sho4qu'), id, {
          start: pageParam,
          limit: SITE_MAPS_PAGE_SIZE,
        })
        .then((res) => res.body ?? []),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === SITE_MAPS_PAGE_SIZE
          ? allPages.length + 1
          : undefined,
    },
  );

export const useReferenceSets = (id: string) =>
  useQuery<MapSiteReferenceSet[]>(referenceSetsQueryKey(id), () =>
    mapSitesAPI
      .getReferenceSets(stroot('yee1ph'), id)
      .then((res) => res.body ?? []),
  );

export const siteForMapQueryKey = (mapId: string) =>
  ['mapSites', 'siteForMap', mapId] as const;

/**
 * The site a map currently belongs to — pre-selects the dropdown in the map
 * browser's "Add to site" modal. `enabled` so it only fetches when the modal
 * opens, and never cached stale: it is invalidated by `invalidateSites` after
 * a reassignment.
 */
export const useSiteForMap = (mapId: string, enabled: boolean) =>
  useQuery<{ id: string; name: string } | null>(
    siteForMapQueryKey(mapId),
    () =>
      mapSitesAPI
        .getSiteForMap(stroot('sit4mp'), mapId)
        .then((res) => res.body?.site ?? null),
    { enabled },
  );

export const siteDemLayersQueryKey = (id: string) =>
  ['mapSites', 'demLayers', id] as const;

// DEM (singleband_cog) layers across the site's maps — the "bring across a DEM"
// candidates in the New map modal. `enabled` so it only fetches when the modal
// opens.
export const useSiteDemLayers = (id: string, enabled: boolean) =>
  useQuery<SiteDemLayer[]>(
    siteDemLayersQueryKey(id),
    () =>
      mapSitesAPI
        .listSiteDemLayers(stroot('dem2li'), id)
        .then((res) => res.body ?? []),
    { enabled },
  );

export const tolerancesQueryKey = (id: string) =>
  ['mapSites', 'tolerances', id] as const;
export const toleranceHistoryQueryKey = (id: string) =>
  ['mapSites', 'toleranceHistory', id] as const;

/**
 * The site's current tolerance plus the defaults to pre-fill when it has none.
 * `current: null` is the normal unconfigured state, not an error.
 */
export const useTolerances = (id: string) =>
  useQuery<ToleranceResponse | undefined>(tolerancesQueryKey(id), () =>
    mapSitesAPI.getTolerances(stroot('tol1gt'), id).then((res) => res.body),
  );

// `enabled` so the history is only fetched when the drawer is opened — the card
// itself only needs the current version.
export const useToleranceHistory = (id: string, enabled: boolean) =>
  useQuery<ToleranceVersion[]>(
    toleranceHistoryQueryKey(id),
    () =>
      mapSitesAPI
        .getToleranceHistory(stroot('tol2hi'), id)
        .then((res) => res.body ?? []),
    { enabled },
  );

/**
 * After a save both the current version AND the history change — a save is an
 * append, so the history grew by one and the tab's version count with it.
 */
export const invalidateTolerances = (siteId: string) => {
  mapSitesQueryClient.invalidateQueries(tolerancesQueryKey(siteId));
  mapSitesQueryClient.invalidateQueries(toleranceHistoryQueryKey(siteId));
};

/** Invalidate everything derived from a site after a mutation. */
export const invalidateSites = (siteId?: string) => {
  mapSitesQueryClient.invalidateQueries(SITES_LIST_QUERY_KEY);
  if (siteId) {
    mapSitesQueryClient.invalidateQueries(siteQueryKey(siteId));
    mapSitesQueryClient.invalidateQueries(siteMapsQueryKey(siteId));
    mapSitesQueryClient.invalidateQueries(referenceSetsQueryKey(siteId));
  }
};

/** Invalidate a map's "which site am I in" lookup after (re)assigning it. */
export const invalidateSiteForMap = (mapId: string) => {
  mapSitesQueryClient.invalidateQueries(siteForMapQueryKey(mapId));
};
